import React, { useState } from "react";
import { FROSTED_PANEL_STYLE } from "./constants";

const DEFAULT_GAME_CONFIG = {
  type: "progressive",
  gameName: "",
  dailyTarget: 100,
  pointsRule: { metricAmount: 1, pointsEarned: 10 },
  effectType: "positive",
  valueType: "monetary",
};

const inputBaseClass = "w-full bg-gray-50 border border-gray-200 px-5 py-3 rounded-[16px] font-normal text-gray-900 focus:border-[var(--gmb-primary)] focus:bg-white outline-none transition-all placeholder:text-gray-400 placeholder:font-light";

function GamifyWorkspaceModal({ metric, workspaces, allMetrics, onClose, onContinue }) {
  const [selectedWsId, setSelectedWsId] = useState(null);
  const [search, setSearch] = useState("");
  const [step, setStep] = useState("select"); // "select" | "config"
  const [gameStep, setGameStep] = useState(1);
  const [gameConfig, setGameConfig] = useState({
    ...DEFAULT_GAME_CONFIG,
    gameName: metric?.name || "",
    dailyTarget: typeof metric?.gameConfig?.dailyTarget === "number" ? metric.gameConfig.dailyTarget : 100,
    pointsRule: metric?.gameConfig?.pointsRule && typeof metric.gameConfig.pointsRule.metricAmount === "number"
      ? { ...metric.gameConfig.pointsRule }
      : { metricAmount: 1, pointsEarned: 10 },
  });

  const filteredWorkspaces = workspaces.filter((ws) =>
    ws.name.toLowerCase().includes(search.toLowerCase())
  );
  const selectedWorkspace = workspaces.find((w) => w.id === selectedWsId);

  const getWorkspaceGamificationStatus = (wsId) => {
    if (metric._source === "companyKpi" && metric.ownerWorkspaceId === wsId && metric.gameConfig?.status === "live") {
      return true;
    }
    const wsMetric = allMetrics.find((m) => m.id === metric.id && m.workspaceId === wsId);
    return wsMetric?.gameConfig?.status === "live";
  };

  const handleContinueToConfig = () => {
    if (selectedWsId) setStep("config");
  };

  const handleBackFromConfig = () => {
    if (gameStep > 1) setGameStep(gameStep - 1);
    else setStep("select");
  };

  const handleNextOrUpdate = () => {
    if (gameStep < 3) setGameStep(gameStep + 1);
    else onContinue(selectedWsId, gameConfig);
  };

  const headerTitle = step === "select" ? "Gamify metric" : "Configure gamification";
  let headerSub = "Pick a workspace — gamification is set up at the workspace level.";
  if (step === "config") {
    headerSub = selectedWorkspace ? `Configuring for ${selectedWorkspace.name}. Set targets and point rules, then launch.` : "";
  }

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/15 backdrop-blur-[3px]" onClick={onClose} />
      <div className={`rounded-lg relative z-10 flex flex-col overflow-hidden max-h-[85vh] w-full ${step === "select" ? "max-w-[440px]" : "max-w-[560px]"}`} style={FROSTED_PANEL_STYLE}>
        {/* Header — icon + title + subtitle, X close top-right (shared across steps) */}
        <header className="flex items-start justify-between px-[20px] py-[16px] border-b border-[rgba(15,23,42,0.06)] flex-shrink-0">
          <div className="flex items-start gap-[11px] min-w-0">
            <div className="w-[34px] h-[34px] rounded-lg flex-shrink-0 flex items-center justify-center bg-[var(--gmb-primary-soft)] text-[var(--gmb-primary)]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-[17px] h-[17px]">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                <path d="M20 3v4" /><path d="M22 5h-4" /><path d="M4 17v2" /><path d="M5 18H3" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold text-gray-900 leading-[1.25]">{headerTitle}</h3>
              {headerSub && <p className="text-[12.5px] text-gray-500 mt-[3px] leading-[1.4]">{headerSub}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-[30px] h-[30px] rounded-[7px] flex-shrink-0 flex items-center justify-center bg-[rgba(15,23,42,0.04)] text-gray-400 hover:bg-[rgba(15,23,42,0.07)] hover:text-gray-600 transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-[15px] h-[15px]" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {step === "select" ? (
          <div className="px-[20px] py-[16px] flex flex-col flex-grow min-h-0">
            <div className="relative mb-[12px]">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="absolute left-[12px] top-1/2 -translate-y-1/2 w-[15px] h-[15px] text-gray-400">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Search workspaces and sub workspaces…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-[34px] pr-[12px] py-[9px] bg-[#f8fafc] border border-[#eef0f2] rounded-[9px] text-[13px] focus:border-[var(--gmb-primary)] focus:bg-white outline-none transition-all"
              />
            </div>
            <div className="flex flex-col gap-[7px] max-h-[280px] overflow-y-auto pr-[4px] custom-scrollbar">
              {filteredWorkspaces.map((ws) => {
                const isGamified = getWorkspaceGamificationStatus(ws.id);
                return (
                  <button
                    key={ws.id}
                    type="button"
                    disabled={isGamified}
                    onClick={() => !isGamified && setSelectedWsId(ws.id)}
                    className={(() => {
                      if (isGamified) return "w-full flex items-center gap-[12px] p-[10px] px-[12px] rounded-[10px] border text-left transition-all border-[#eef0f2] bg-[#f8fafc] opacity-60 cursor-not-allowed";
                      if (selectedWsId === ws.id) return "w-full flex items-center gap-[12px] p-[10px] px-[12px] rounded-[10px] border text-left transition-all border-[var(--gmb-primary)] bg-[var(--gmb-primary-soft)] cursor-pointer";
                      return "w-full flex items-center gap-[12px] p-[10px] px-[12px] rounded-[10px] border text-left transition-all border-[#eef0f2] hover:border-[#dde2e8] bg-white cursor-pointer";
                    })()}
                  >
                    <div className={`w-[32px] h-[32px] rounded-lg overflow-hidden border border-[#eef0f2] shadow-sm flex-shrink-0 bg-gray-100 flex items-center justify-center ${isGamified ? "grayscale opacity-50" : ""}`}>
                      {ws.icon ? <img src={ws.icon} alt={ws.name} className="w-full h-full object-cover" /> : <span className="text-gray-400 text-sm font-medium">W</span>}
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className={`font-medium text-[13.5px] truncate ${selectedWsId === ws.id ? "text-[var(--gmb-primary)]" : "text-gray-700"} ${isGamified ? "text-gray-400" : ""}`}>
                        {ws.name}
                      </p>
                      {isGamified && (
                        <div className="flex items-center gap-[5px] mt-[2px]">
                          <span className="w-[5px] h-[5px] bg-emerald-500 rounded-full" />
                          <span className="text-[10.5px] font-medium text-emerald-600">Already gamified</span>
                        </div>
                      )}
                    </div>
                    <div
                      className={(() => {
                        if (selectedWsId === ws.id) return "w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-all bg-[var(--gmb-primary)] border-[var(--gmb-primary)]";
                        if (isGamified) return "w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-all bg-gray-100 border-gray-200";
                        return "w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center transition-all bg-white border-gray-300";
                      })()}
                    >
                      {selectedWsId === ws.id && <div className="w-[6px] h-[6px] rounded-full bg-white" />}
                    </div>
                  </button>
                );
              })}
              {filteredWorkspaces.length === 0 && (
                <div className="py-[40px] text-center opacity-50">
                  <p className="text-[13px] font-medium text-gray-400">No matching workspaces or sub workspaces</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-grow overflow-y-auto px-[24px] py-[18px]">
            {/* Step indicator */}
            <div className="flex items-center justify-center gap-[8px] mb-[18px]">
              {[1, 2, 3].map((s) => {
                let stepCircleClass = "w-8 h-8 rounded-lg flex items-center justify-center font-medium text-[11px] transition-all bg-gray-100 text-gray-400";
                if (gameStep === s) stepCircleClass = "w-8 h-8 rounded-lg flex items-center justify-center font-medium text-[11px] transition-all bg-[var(--gmb-primary)] text-white shadow-sm";
                else if (gameStep > s) stepCircleClass = "w-8 h-8 rounded-lg flex items-center justify-center font-medium text-[11px] transition-all bg-[var(--gmb-primary-tint)] text-[var(--gmb-primary)]";
                return (
                  <div key={s} className="flex items-center">
                    <div className={stepCircleClass}>
                      {gameStep > s ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" className="w-[14px] h-[14px]"><polyline points="20 6 9 17 4 12" /></svg> : `0${s}`}
                    </div>
                    {s < 3 && <div className={`w-[40px] h-0.5 mx-[8px] rounded-full ${gameStep > s ? "bg-[var(--gmb-primary-tint)]" : "bg-gray-200"}`} />}
                  </div>
                );
              })}
            </div>

            <div className="w-full">
              {gameStep === 1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px] w-full">
                  {["progressive", "anchor"].map((type) => (
                    <div key={type} className={`p-[18px] rounded-xl border text-left bg-white transition-all flex flex-col ${gameConfig.type === type ? "border-[var(--gmb-primary)] shadow-md" : "border-gray-100"}`}>
                      <div className="mb-[14px] text-[var(--gmb-primary)]">
                        {type === "progressive" ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" /></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><circle cx="12" cy="5" r="3" /><path d="M12 8v14" /><path d="M5 12H2a10 10 0 0 0 20 0h-3" /></svg>
                        )}
                      </div>
                      <h4 className="text-[15px] font-semibold text-gray-900 mb-[4px] capitalize">{type}</h4>
                      <p className="text-gray-500 text-[12px] font-normal leading-relaxed mb-[14px]">{type === "progressive" ? "Track continuous progress towards a milestone." : "Hold a specific value or threshold over time."}</p>
                      <button type="button" onClick={() => setGameConfig({ ...gameConfig, type })} className={`w-full py-[8px] rounded-lg font-medium text-[12px] transition-all ${gameConfig.type === type ? "bg-[var(--gmb-primary)] text-white shadow-sm" : "bg-gray-50 text-gray-400 hover:bg-gray-100"}`}>Select</button>
                    </div>
                  ))}
                </div>
              )}
              {gameStep === 2 && (
                <div className="bg-white p-[18px] rounded-xl border border-gray-100 shadow-sm flex flex-col gap-[16px]">
                  <section>
                    <label className="text-[11px] font-medium text-gray-400 block mb-[6px]">Game name (player-facing)</label>
                    <input type="text" value={gameConfig.gameName} placeholder="e.g. Sales Rockstar Challenge" onChange={(e) => setGameConfig({ ...gameConfig, gameName: e.target.value })} className={inputBaseClass} />
                  </section>
                  <section>
                    <label className="text-[11px] font-medium text-gray-400 block mb-[6px]">Daily target</label>
                    <input type="number" value={gameConfig.dailyTarget} placeholder="e.g. 1000" onChange={(e) => setGameConfig({ ...gameConfig, dailyTarget: Number(e.target.value) || 0 })} className={inputBaseClass} />
                    <p className="text-[10px] text-gray-400 mt-[6px]">This value becomes the primary display on your metric card.</p>
                  </section>
                  <section>
                    <label className="text-[11px] font-medium text-gray-400 block mb-[8px]">Points rule</label>
                    <div className="bg-gray-50 p-[16px] rounded-xl border border-gray-100 flex flex-col gap-[12px]">
                      <div className="flex items-center gap-[10px] flex-wrap">
                        <span className="text-[13px] font-medium text-gray-600">If a player achieves</span>
                        <input type="number" value={gameConfig.pointsRule.metricAmount} onChange={(e) => setGameConfig({ ...gameConfig, pointsRule: { ...gameConfig.pointsRule, metricAmount: Number(e.target.value) || 0 } })} className="w-[72px] bg-white border border-gray-200 p-[8px] rounded-lg text-center text-[13px] font-medium text-gray-900 focus:border-[var(--gmb-primary)] outline-none" />
                        <span className="text-[13px] font-medium text-[var(--gmb-primary)]">{gameConfig.gameName || "metric"}</span>
                      </div>
                      <div className="flex items-center gap-[10px]">
                        <span className="text-[13px] font-medium text-gray-600">They earn</span>
                        <input type="number" value={gameConfig.pointsRule.pointsEarned} onChange={(e) => setGameConfig({ ...gameConfig, pointsRule: { ...gameConfig.pointsRule, pointsEarned: Number(e.target.value) || 0 } })} className="w-[72px] bg-[var(--gmb-primary-soft)] border border-[var(--gmb-primary-tint)] p-[8px] rounded-lg text-center text-[13px] font-medium text-[var(--gmb-primary)] outline-none" />
                        <span className="text-[13px] font-medium text-gray-600">points</span>
                      </div>
                    </div>
                  </section>
                </div>
              )}
              {gameStep === 3 && (
                <div className="bg-white p-[18px] rounded-xl border border-gray-50 shadow-sm flex flex-col gap-[18px]">
                  <div className="flex flex-col gap-[8px]">
                    <label className="text-[11px] font-medium text-gray-400 pl-1">Effect type</label>
                    <div className="bg-gray-50 p-[8px] rounded-xl flex gap-[8px] border border-gray-100/50">
                      {["positive", "negative"].map((eff) => (
                        <button key={eff} type="button" onClick={() => setGameConfig({ ...gameConfig, effectType: eff })} className={`flex-grow py-[12px] px-[16px] rounded-lg transition-all flex flex-col items-center justify-center text-center ${gameConfig.effectType === eff ? "bg-white text-gray-900 shadow-sm border border-gray-100" : "text-gray-400 hover:text-gray-600 border border-transparent hover:bg-white/30"}`}>
                          <span className="text-[14px] font-medium block mb-[2px]">{eff === "positive" ? "Positive" : "Negative"}</span>
                          <span className="text-[11px] font-normal opacity-60 leading-tight">{eff === "positive" ? "Increase is better" : "Decrease is better"}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-[8px]">
                    <label className="text-[11px] font-medium text-gray-400 pl-1">Value type</label>
                    <div className="bg-gray-50 p-[8px] rounded-xl flex gap-[8px] border border-gray-100/50">
                      {["monetary", "item"].map((val) => (
                        <button key={val} type="button" onClick={() => setGameConfig({ ...gameConfig, valueType: val })} className={`flex-grow py-[12px] px-[16px] rounded-lg transition-all flex flex-col items-center justify-center text-center ${gameConfig.valueType === val ? "bg-white text-gray-900 shadow-sm border border-gray-100" : "text-gray-400 hover:text-gray-600 border border-transparent hover:bg-white/30"}`}>
                          <span className="text-[14px] font-medium block mb-[2px]">{val === "monetary" ? "Monetary" : "Unit / Count"}</span>
                          <span className="text-[11px] font-normal opacity-60 leading-tight">{val === "monetary" ? "Tracking $ value" : "Tracks raw units"}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer — grouped bottom-right on every step */}
        <footer className="flex items-center justify-end gap-[10px] px-[20px] py-[14px] border-t border-[rgba(15,23,42,0.06)] bg-[rgba(248,250,252,0.5)] flex-shrink-0">
          <button
            type="button"
            onClick={step === "config" ? handleBackFromConfig : onClose}
            className="px-[24px] py-[8px] rounded-lg font-medium text-[13px] text-slate-600 border border-[rgba(15,23,42,0.1)] hover:bg-slate-50 transition-colors"
          >
            {step === "config" && gameStep > 1 ? "Back" : "Cancel"}
          </button>
          {step === "select" ? (
            <button
              type="button"
              disabled={!selectedWsId}
              onClick={() => selectedWsId && handleContinueToConfig()}
              className="px-[24px] py-[8px] bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-[13px] shadow-sm hover:brightness-95 transition-all disabled:opacity-40 disabled:pointer-events-none"
            >
              Continue to gamification
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNextOrUpdate}
              className="px-[24px] py-[8px] bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-[13px] shadow-sm hover:brightness-95 transition-all"
            >
              {gameStep < 3 ? "Next" : "Launch game"}
            </button>
          )}
        </footer>
      </div>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 10px; }
      `}</style>
    </div>
  );
}

export default GamifyWorkspaceModal;
